import store from "@/store";
import { InspectDetail } from "@/model/InspectDetail";
import { OptionData, OptionItem } from "@/model/Option";

const namespace = "InspectionDetail";

export class InspectionOptionDetailStoreService {
  private optionList: OptionItem[] = [];
  deSelOption: boolean = false;

  // サービス初期化時にオプションの選択管理枠をStoreへ登録する
  constructor() {
    this.addNowSelected();
  }

  // オプションが選択済みかどうかを参照する
  get nowSelected(): boolean {
    const nowSelected = store.getters[`${namespace}/getNowSelected`];
    if (nowSelected.option === true || nowSelected.option === false) {
      return nowSelected.option;
    }
    this.addNowSelected();
    return false;
  }

  // オプションの選択管理枠をStoreへ追加する
  addNowSelected() {
    return store.dispatch(`${namespace}/addNowSelected`, "option");
  }

  // オプションの選択済み状態をStoreへ反映する
  setNowSelected(payload: boolean) {
    return store.dispatch(`${namespace}/setNowSelected`, {
      key: "option",
      value: payload
    });
  }

  // 共通処理から扱うオプション一覧を参照する
  get list(): OptionItem[] {
    return this.optionList;
  }

  // 共通処理からオプション一覧を保持する
  setList(payload: OptionItem[]) {
    this.optionList = payload;
  }

  // オプションの選択状態からスタート可否判定用の状態を更新する
  checkDisableStart(arrData: OptionItem[], isUnSelect?: boolean) {
    let checked: boolean = false;
    if (isUnSelect) {
      checked = true;
    } else if (arrData) {
      checked = arrData.some((data: OptionItem) => data.isCheck === 1);
    }
    this.setNowSelected(checked);
  }

  // テンプレート表示用のオプション選択肢リストを生成する
  getTargetList() {
    return this.list.map((data: OptionItem) => {
      const key = data.optionId || data.num;
      return {
        "mode": 3,
        "btnDel": false,
        "btnSel": data.isCheck === 1,
        "btnFre": !(data.isCheck === 1),
        "btnUse": false,
        "key": key,
        "dataSet": key,
        "isDisabled": false,
        "icon": "",
        "label": data.optionNm,
        "labelEntends1": data.kingakuZn,
        "labelEntends2": data.kingakuZk,
      };
    });
  }

  // スタート時に選択中のオプションを保存データへ反映する
  setStartInspectDetail(inspectionDetail: InspectDetail) {
    inspectionDetail.option = this.list
      .filter((data: OptionItem) => data.isCheck === 1)
      .map((data: OptionItem) => {
        return {
          num: data.num,
          optionId: data.optionId,
          optionNm: data.optionNm,
          kingakuZn: data.kingakuZn,
          kingakuZk: data.kingakuZk,
          renban: data.renban,
          warrantyKbn: data.warrantyKbn,
          isInport: data.isInport,
          kijunCd: data.kijunCd,
          periodCd: data.periodCd,
          tireCd: data.tireCd
        };
      });
  }

  // 取得済みの検査構成からオプションの初期選択情報を反映する
  applyInitialInspectionDetail(data: any, inspectionDetail?: InspectDetail) {
    if (inspectionDetail) {
      inspectionDetail.option = data.option || [];
    }
    return { data, inspectionDetail };
  }

  // オプションの選択クリックを処理する
  select(key: number) {
    const optionList = this.list.map((data: OptionItem) => {
      const dataKey = data.optionId || data.num;
      if (dataKey == key) {
        const isChecked = data.isCheck === 1;
        data.isCheck = isChecked ? 0 : 1;
        if (!isChecked) {
          this.deSelOption = false;
        }
      }
      return data;
    });
    this.setList(optionList);
    this.checkDisableStart(optionList, this.deSelOption);
  }

  // オプションで「選択無し」を表示できるか返す
  canDeSelect() {
    return true;
  }

  // オプションが「選択無し」状態か返す
  isDeSelected() {
    return this.deSelOption;
  }

  // オプションの「選択無し」状態を切り替える
  deSelect() {
    const optionList = this.list;
    if (!this.deSelOption) {
      optionList.map(data => (data.isCheck = 0));
    }
    this.deSelOption = !this.deSelOption;
    this.setList([...optionList]);
    this.checkDisableStart(optionList, this.deSelOption);
    return this.deSelOption;
  }

  // 共通呼び出し用の「選択無し」クリック処理
  clickDeSelect() {
    return this.deSelect();
  }

  // Storeに既存リストがある場合に選択状態を再評価する
  applyExistingList() {
    const optionList = this.list;
    this.checkDisableStart(optionList, this.deSelOption);
    return optionList;
  }

  // API取得結果からオプション一覧の初期選択状態を作成する
  setInitialList(res: OptionData, selectedOption: OptionItem[] | undefined, initialDeSelected?: boolean) {
    const responseItems = res && res.option && res.option.items ? res.option.items : [];
    const optionList = responseItems.map((data: OptionItem) => {
      const selected = selectedOption
        ? selectedOption.some((option: OptionItem) =>
          data.optionId == option.optionId
          && (data.optionId !== 0 || data.renban === option.renban)
        )
        : false;
      data.isCheck = selected || data.isCheck === 1 ? 1 : 0;
      return data;
    });
    this.deSelOption = initialDeSelected === true
      && !optionList.some((data: OptionItem) => data.isCheck === 1);
    this.setList(optionList);
    this.checkDisableStart(optionList, this.deSelOption);
    return optionList;
  }

  // オプション一覧を既存保持データまたはAPIから取得する
  getDataList(
    loadInitialList: () => Promise<OptionData>,
    selectedOption: OptionItem[] | undefined,
    initialDeSelected?: boolean
  ) {
    if (this.list.length > 0) {
      this.applyExistingList();
      return {
        hasExistingList: true,
        promise: null
      };
    }
    return {
      hasExistingList: false,
      promise: loadInitialList().then((res: OptionData) => {
        return this.setInitialList(res, selectedOption, initialDeSelected);
      })
    };
  }
}
